import React, { useContext } from 'react';
import ClientProvider from '../../../../Various/ClientProvider';
import CampaignsOverview from '../shared/CampaignsOverview';
import LoadCampaignDataButton from '../shared/LoadCampaignDataButton';
import ActionButtonGroup, { ActionButton } from '../shared/ActionButtonGroup';
import { iterateCampaigns, initializeTableValues } from '../../utils/campaignHelpers';
import { useBatchJobSender } from '../../hooks/useBatchJobSender';

const SendInvoicesTab = ({ 
    clientCampaigns, 
    convertTimestampsToDates, 
    onTableRowClick,
    tableValues,
    setTableValues,
    invoicingFilter,
    setInvoicingFilter,
    triggerTableRerender,
    tableRerenderKey = 0,
    loadAllCampaignsWithHistoryAndOpenBillingData
}) => {
    const wsProvider = useContext(ClientProvider);

    const handleAutoSelect = () => {
        const newTableValues = initializeTableValues(tableValues, 'sendInvoices');
        let hasChanges = false;

        iterateCampaigns(clientCampaigns, convertTimestampsToDates, (campaign, campaignId) => {
            // Only process campaigns with open billing amounts
            if (campaign.open_billing_data) {
                // Set default dates
                if (!newTableValues.sendInvoicesFromDate[campaignId]) {
                    newTableValues.sendInvoicesFromDate[campaignId] = campaign.open_billing_data.start_date;
                    hasChanges = true;
                }
                if (!newTableValues.sendInvoicesToDate[campaignId]) {
                    newTableValues.sendInvoicesToDate[campaignId] = campaign.open_billing_data.end_date;
                    hasChanges = true;
                }
                
                // Check if invoice is ready
                if (campaign.open_billing_data.invoice_ready) {
                    // Set default action based on invoice readiness
                    const currentAction = newTableValues.sendInvoicesAction[campaignId];
                    if (!currentAction || currentAction === 'no_action') {
                        newTableValues.sendInvoicesAction[campaignId] = 'send_report_and_invoice';
                        hasChanges = true;
                    }
                }
            }
        });

        if (hasChanges) {
            setTableValues(newTableValues);
            triggerTableRerender();
        } else {
            alert('No changes were made. All campaigns may already be configured or no campaigns meet the auto-selection criteria.');
        }
    };

    const handleSelectAll = () => {
        const newTableValues = initializeTableValues(tableValues, 'sendInvoices');
        let hasChanges = false;

        iterateCampaigns(clientCampaigns, convertTimestampsToDates, (campaign, campaignId) => {
            // Only process campaigns with open billing amounts
            if (campaign.open_billing_data) {
                // Set default dates
                if (!newTableValues.sendInvoicesFromDate[campaignId]) {
                    newTableValues.sendInvoicesFromDate[campaignId] = campaign.open_billing_data.start_date;
                    hasChanges = true;
                }
                if (!newTableValues.sendInvoicesToDate[campaignId]) {
                    newTableValues.sendInvoicesToDate[campaignId] = campaign.open_billing_data.end_date;
                    hasChanges = true;
                }
                
                // Select all campaigns with open billing amounts
                newTableValues.sendInvoicesAction[campaignId] = 'send_report_and_invoice';
                hasChanges = true;
            }
        });

        if (hasChanges) {
            setTableValues(newTableValues);
            triggerTableRerender();
        } else {
            alert('No campaigns with open billing amounts found.');
        }
    };

    const handleDeselectAll = () => {
        const newTableValues = initializeTableValues(tableValues, 'sendInvoices');
        let hasChanges = false;

        iterateCampaigns(clientCampaigns, convertTimestampsToDates, (campaign, campaignId) => {
            // Only process campaigns with open billing amounts
            if (campaign.open_billing_data) {
                // Deselect all campaigns
                newTableValues.sendInvoicesAction[campaignId] = 'no_action';
                hasChanges = true;
            }
        });

        if (hasChanges) {
            setTableValues(newTableValues);
            triggerTableRerender();
        } else {
            alert('No campaigns were selected to deselect.');
        }
    };

    const handleSendSelectedInvoices = useBatchJobSender({
        wsProvider,
        clientCampaigns,
        tableValues,
        actionKey: 'sendInvoicesAction',
        fromDateKey: 'sendInvoicesFromDate',
        toDateKey: 'sendInvoicesToDate',
        actionValue: 'send_report_and_invoice',
        createInvoice: true,
        attachInvoice: true,
        updateReportHistory: true,
        triggerTableRerender,
        emptyMessage: 'Please select at least one campaign to send invoices for.'
    });

    return (
        <div>
            <LoadCampaignDataButton
                clientCampaigns={clientCampaigns}
                loadAllCampaignsWithHistoryAndOpenBillingData={loadAllCampaignsWithHistoryAndOpenBillingData}
                triggerTableRerender={triggerTableRerender}
                tabName="Send Invoices"
            />

            <ActionButtonGroup>
                <ActionButton 
                    label="Auto-Select" 
                    onClick={handleAutoSelect}
                    variant="success"
                />
                <ActionButton 
                    label="Select All" 
                    onClick={handleSelectAll}
                    variant="info"
                />
                <ActionButton 
                    label="Deselect All" 
                    onClick={handleDeselectAll}
                    variant="secondary"
                />
                <ActionButton 
                    label="Send Selected Invoices" 
                    onClick={handleSendSelectedInvoices}
                    variant="primary"
                />
            </ActionButtonGroup>

            <CampaignsOverview
                clientCampaigns={clientCampaigns}
                convertTimestampsToDates={convertTimestampsToDates}
                onTableRowClick={onTableRowClick}
                tableValues={tableValues}
                setTableValues={setTableValues}
                invoicingFilter={invoicingFilter}
                setInvoicingFilter={setInvoicingFilter}
                columnsToShow="send_invoices"
                tableRerenderKey={tableRerenderKey} // This will be updated by the parent component
            />
        </div>
    );
};

export default SendInvoicesTab; 